/**
 * A refused request, read the way the access screens need it.
 *
 * The module answers `{ error: "<German sentence>", ...extra }`; the tool's own
 * routes answer RFC 7807 with a German `detail`. Either way the sentence is
 * the server's and is shown as it is - the screens never invent a reason the
 * server did not give ([F-027]: "eine fachliche Meldung").
 *
 * The extras the screens branch on:
 *   - `requires: "elevation"` - the session must be elevated first ([F-033]);
 *     `elevation: true` is read the same, for a route that says it that way;
 *   - `requires: "last-admin-code"` - the step would leave no active
 *     administrator; a code went to the system account ([F-032]);
 *   - `requires: "retry"` - the administration changed under the request;
 *   - `requires: "roles"` - a first login for an account without roles;
 *   - `suggestion` - a free variation of a display name that is taken ([DO-012]);
 *   - `system: true` - the reset was asked for the system account's address.
 */
export interface AccessFailure {
  status: number | undefined;
  message: string;
  requires: string | null;
  needsElevation: boolean;
  suggestion: string | null;
  body: Record<string, unknown>;
}

/** `fallback` is the screen's German sentence (i18n) for a refusal without one. */
export function readFailure(error: unknown, fallback: string): AccessFailure {
  const augmented = (error && typeof error === "object" ? error : {}) as {
    status?: unknown;
    body?: unknown;
  };
  const body =
    augmented.body && typeof augmented.body === "object" ? (augmented.body as Record<string, unknown>) : {};
  const status = typeof augmented.status === "number" ? augmented.status : undefined;
  const fromBody =
    typeof body.detail === "string" && body.detail
      ? body.detail
      : typeof body.error === "string" && body.error
        ? body.error
        : null;
  // Without a sentence from the server (a network failure, a bare status),
  // the screen's own German fallback - never the browser's English text.
  const message = fromBody ?? fallback;
  const requires = typeof body.requires === "string" ? body.requires : null;
  return {
    status,
    message,
    requires,
    needsElevation: requires === "elevation" || body.elevation === true,
    suggestion: typeof body.suggestion === "string" && body.suggestion ? body.suggestion : null,
    body,
  };
}
